import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, RouteLocationRaw } from 'vue-router';
import { useCartStore } from '@/features/Cart/stores/cart';
import { cartActions } from '@/features/Cart/services/cartService';
import {
  getLineIdentity,
  pickCarryOverLineItemProperties,
} from '@/features/Cart/services/lineItemProperties';
import { fitsProjectedCart } from '@/features/Cart/services/cartStockSwap';
import { findVariantParentMaxQuantityFamilies } from '@/features/Cart/cartRules/variantParentMaxQuantity';
import type { AddToCartItem, ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type { CartLineRemoval } from '@/features/Cart/types/models';
import { useProductCompleteStore } from './productComplete';
import { useProductBaseStore } from './productBase';
import { useProductBundleStore } from './productBundle';
import { useProductPageBundleStore } from './productPageBundle';
import { useProductPageDetails } from './productPageDetails';
import { useProductStockStore } from './productStock';
import { allUpsells } from '../componentData/productUpsellData';
import {
  useProductUpsellColourPresets,
  useProductUpsellLayoutPresets,
} from '../composables/productUpsellPresets';
import type { BundleItem, ProductComplete } from '../types/models';
import type {
  ProductDetails,
  UpsellDetails,
  UpsellConfiguration,
  UpsellVariantMatch,
  DisplayRequirements,
  UpsellProps,
} from '../types/upsellTypes';
import { getProductValueByPath } from '@/features/Product/services/storeProducts';
import { useSystemStore } from '@/stores/system';
import { useDeviceSize } from '@/composables/useDeviceSize';
import { resolveChoiceGroupSelection } from '@/composables/regionHelpers';
import { isSkuVariantOfBase } from '@/composables/productHelper';
import { useProductAtc, type LoadingState } from '../composables/productAddToCartHelper';
import { getChoiceGroupFillCount } from '../composables/useBundleItemTotal';
import { resolveBaseProductPath } from '../composables/productRandomisation';
import { useBundleBuilder } from '../composables/useBundleBuilder';

type Sku = string;

type UpsellActionAddition = {
  product: ProductComplete;
  item: AddToCartItem;
};

type ChoiceGroupTarget = {
  sku?: Sku;
  fallbackSku?: Sku;
  quantity?: number;
  variantMatch?: boolean;
};

export const useProductUpsellStore = defineStore('productUpsellStore', () => {
  const productCompleteStore = useProductCompleteStore();
  const productBundleStore = useProductBundleStore();
  const productBaseStore = useProductBaseStore();
  const pageBundleStore = useProductPageBundleStore();
  const pageDetailsStore = useProductPageDetails();
  const cartStore = useCartStore();
  const productStockStore = useProductStockStore();
  const systemStore = useSystemStore();
  const route = useRoute();
  const isValidating = ref(false);
  const isHandlingUpsellAction = ref(false);
  const upsellActionErrorText = ref('');
  const lockedUpsell = ref<UpsellConfiguration | null>(null);

  const isUpsellDetails = (item: ProductDetails | UpsellDetails): item is UpsellDetails =>
    'upsell' in item;

  const toRulesArray = (
    input?: (ProductDetails | UpsellDetails) | (ProductDetails | UpsellDetails)[],
  ) =>
    (Array.isArray(input) ? input : input ? [input] : []).filter((p) =>
      isUpsellDetails(p) ? !!p.upsell : !!p.product,
    );

  const normaliseRules = (rules?: DisplayRequirements) => ({
    anyOf: toRulesArray(rules?.anyOf),
    allOf: toRulesArray(rules?.allOf),
  });

  const getCurrentPath = () => route.path || systemStore.currentPath || window.location.pathname;

  const getProductIdentifier = (
    product: string | ProductComplete | undefined | null,
  ): string | null => {
    if (!product) return null;
    if (typeof product === 'string') return product;
    return product.attributes?.sku || product.attributes?.handle || null;
  };

  const resolveProduct = (
    productRef: string | ProductComplete | undefined | null,
  ): ProductComplete | null => {
    if (!productRef) return null;
    if (typeof productRef !== 'string') {
      return productRef;
    }
    let product = productCompleteStore.getCompleteProduct('attributes.sku', productRef);
    if (!product) {
      product = productCompleteStore.getCompleteProduct('attributes.handle', productRef);
    }
    if (!product) {
      product = productBundleStore.completeBundleProducts.find(
        (p) =>
          getProductValueByPath(p, 'attributes.sku') === productRef ||
          getProductValueByPath(p, 'attributes.handle') === productRef,
      );
    }
    if (!product) {
      product = productBaseStore.completeBaseProducts.find((p) => {
        return (
          getProductValueByPath(p, 'attributes.sku') === productRef ||
          getProductValueByPath(p, 'attributes.handle') === productRef
        );
      });
    }
    return product || null;
  };

  const resolveProductWithProcessedItems = (
    productRef: string | ProductComplete | undefined | null,
  ): ProductComplete | null => {
    const resolved = resolveProduct(productRef);
    if (!resolved?.bundleDefinition?.bundleItems) return resolved;
    const bundleItems = resolved.bundleDefinition.bundleItems.map((item) => {
      if (item.processedProducts?.length) return item;
      const hydrated = item.productIds
        .map(
          (variantId) =>
            productBaseStore.completeBaseProducts.find(
              (p) => p.attributes.variantId === variantId,
            ) ?? productCompleteStore.getCompleteProduct('attributes.variantId', variantId),
        )
        .filter((p): p is ProductComplete => !!p);

      return { ...item, processedProducts: hydrated };
    });
    return {
      ...resolved,
      bundleDefinition: { ...resolved.bundleDefinition, bundleItems },
    };
  };

  const isProductSoldOut = (product: ProductComplete | null): boolean =>
    !product || productStockStore.isProductSoldOut(product);

  const topLevelSkusInCart = computed(
    () => new Set(cartStore.items.map((item) => item.sku).filter(Boolean)),
  );

  const cartHasSku = (sku: Sku, requiredQuantity?: number) => {
    if (requiredQuantity != null) return cartStore.cartHasSkuQuantity(sku, requiredQuantity);
    return topLevelSkusInCart.value.has(sku);
  };

  const isProductInCart = (
    productRef: string | ProductComplete,
    requiredQuantity?: number,
  ): boolean => {
    const identifier = getProductIdentifier(productRef);
    if (!identifier) return false;
    return cartHasSku(identifier, requiredQuantity);
  };

  const itemMatches = (item: ProductDetails | UpsellDetails): boolean => {
    if (isUpsellDetails(item)) {
      return activeUpsellIdsInCart.value.has(item.upsell);
    }
    return isProductInCart(item.product, item.quantity);
  };

  const passesRegionRules = (excludedRegions?: string[], includedRegions?: string[]) => {
    const currentRegion = systemStore.region?.toUpperCase?.() || '';
    if (includedRegions?.length) {
      if (!includedRegions.map((r) => r.toUpperCase()).includes(currentRegion)) return false;
    }
    if (excludedRegions?.length) {
      if (excludedRegions.map((r) => r.toUpperCase()).includes(currentRegion)) return false;
    }
    return true;
  };

  const passesLocationRules = (location?: UpsellConfiguration['displayRules']['location']) => {
    if (!location) return true;
    const currentPath = getCurrentPath();
    if (currentPath.includes('/cart') && cartStore.itemCount === 0) return false;
    const resolvedCurrentPath = resolveBaseProductPath(currentPath);
    const matchesPath = (pattern: string) => {
      const resolvedPattern = resolveBaseProductPath(pattern);
      return (
        resolvedCurrentPath === resolvedPattern ||
        resolvedCurrentPath.startsWith(resolvedPattern + '/')
      );
    };
    if (location.exclude?.some(matchesPath)) return false;
    return !location.include?.length || location.include.some(matchesPath);
  };

  const evaluateProductRules = (rules?: DisplayRequirements): boolean => {
    if (!rules) return false;
    const { anyOf, allOf } = normaliseRules(rules);
    if (!anyOf.length && !allOf.length) return false;
    const anyOfPasses = !anyOf.length || anyOf.some(itemMatches);
    const allOfPasses = !allOf.length || allOf.every(itemMatches);
    return anyOfPasses && allOfPasses;
  };

  const passesIncludeRules = (rules?: DisplayRequirements): boolean =>
    !rules || evaluateProductRules(rules);

  const passesExcludeRules = (rules?: DisplayRequirements): boolean =>
    !!rules && evaluateProductRules(rules);

  const actionStockCache = new Map<string, boolean>();
  let actionStockCacheVersion = '';

  const getActionRemovals = (config: UpsellConfiguration): CartLineRemoval[] => {
    const unavailableLineKeys = new Set(productStockStore.unavailableLines);
    return (config.actions?.remove ?? []).reduce<CartLineRemoval[]>(
      (removals, { product, quantity }) => {
        const identifier = getProductIdentifier(product);
        if (!identifier) return removals;
        const matchingLines = cartStore.items.filter(
          (item) =>
            (item.sku === identifier || item.handle === identifier) &&
            !cartStore.isFreeGiftItem(item) &&
            !unavailableLineKeys.has(item.key) &&
            !removals.some(({ key }) => key === item.key),
        );
        const removedLines = quantity ? matchingLines.slice(0, 1) : matchingLines;
        return [
          ...removals,
          ...removedLines.map((line) => ({
            key: line.key,
            lineIdentity: getLineIdentity(line.variant_id, line.properties),
            removeQuantity: quantity ? Math.min(quantity, line.quantity) : line.quantity,
          })),
        ];
      },
      [],
    );
  };

  const getRemainingCartAllowance = (
    product: ProductComplete,
    removals: CartLineRemoval[],
  ): number => {
    const quantityInCart = cartStore.items
      .filter((line) => line.variant_id === product.attributes.variantId)
      .reduce(
        (total, line) =>
          total +
          line.quantity -
          (removals.find(({ key }) => key === line.key)?.removeQuantity ?? 0),
        0,
      );
    return (product.attributes.maxCartQuantity ?? 5) - quantityInCart;
  };

  const getActionAddition = (
    config: UpsellConfiguration,
    productRef: string | ProductComplete,
    quantity: number,
    removals: CartLineRemoval[],
  ): UpsellActionAddition | null => {
    const product = resolveProduct(productRef);
    if (!product) return null;
    const isClampedByCartAllowance = !product.bundleDefinition;
    if (isClampedByCartAllowance && getRemainingCartAllowance(product, removals) < quantity) {
      return null;
    }
    if (isProductSoldOut(product)) return null;
    const {
      atcObject: [item],
    } = buildUpsellAtcObject(
      productRef,
      quantity,
      config.id,
      resolveChoiceGroupTarget(config, productRef),
    );
    if (product.bundleDefinition && !item.properties._custom_bundle_details) return null;
    return { product, item };
  };

  const isActionAddable = (config: UpsellConfiguration): boolean => {
    try {
      const removals = getActionRemovals(config);
      const additions = (config.actions?.add ?? []).flatMap(
        ({ product: productRef, quantity = 1 }) =>
          productRef && resolveProduct(productRef)
            ? [getActionAddition(config, productRef, quantity, removals)]
            : [],
      );
      const addableAdditions = additions.filter(
        (addition): addition is UpsellActionAddition => !!addition,
      );
      if (addableAdditions.length < additions.length) return false;
      const fit = fitsProjectedCart(
        addableAdditions.map(({ item }) => item),
        cartStore.cartData,
        {
          removals,
          additionSkus: addableAdditions.map(({ product }) => product.attributes.sku),
        },
        productStockStore.getExactStock,
        findVariantParentMaxQuantityFamilies(),
      );
      return fit === 'fits';
    } catch (error) {
      console.error(`Upsell ${config.id} stock check failed`, error);
      return false;
    }
  };

  const hasStockForActions = (config: UpsellConfiguration): boolean => {
    const cacheVersion = [
      productStockStore.lastUpdated,
      cartSignature.value,
      productStockStore.unavailableLines.join(','),
      cartStore.cartUpdateInProgress,
    ].join('|');
    if (cacheVersion !== actionStockCacheVersion) {
      actionStockCache.clear();
      actionStockCacheVersion = cacheVersion;
    }
    const cacheKey = [
      config.id,
      getCurrentPath(),
      ...(config.actions?.add ?? []).flatMap(({ product: productRef }) => {
        if (!productRef) return [];
        const { sku, fallbackSku } = resolveChoiceGroupTarget(config, productRef);
        return [getProductIdentifier(productRef), sku, fallbackSku];
      }),
    ].join('|');
    const cachedResult = actionStockCache.get(cacheKey);
    if (cachedResult !== undefined) return cachedResult;
    const isAddable = isActionAddable(config);
    actionStockCache.set(cacheKey, isAddable);
    return isAddable;
  };

  const isReplacementOffer = (config: UpsellConfiguration): boolean =>
    !!config.actions?.remove?.length;

  const approveUpsell = (config: UpsellConfiguration) => {
    const product = resolveProduct(config.upsellProduct?.product);
    if (isProductSoldOut(product)) return false;
    if (
      !passesRegionRules(config.displayRules?.excludedRegions, config.displayRules?.includedRegions)
    ) {
      return false;
    }
    if (!passesLocationRules(config.displayRules?.location)) {
      return false;
    }
    if (!passesIncludeRules(config.displayRules?.requiredProductsInCart)) {
      return false;
    }
    if (passesExcludeRules(config.displayRules?.excludedProductsInCart)) {
      return false;
    }
    const requiredInStock = config.displayRules?.requiredProductsInStock;
    if (
      requiredInStock?.some((ref) => {
        const product = resolveProductWithProcessedItems(ref);
        return !product || isProductSoldOut(product);
      })
    ) {
      return false;
    }
    if (isVariantMatch(config.choiceGroupSelection) && !getTriggerVariantProduct(config)) {
      return false;
    }
    if (isReplacementOffer(config) && !getActionRemovals(config).length) return false;
    if (!hasStockForActions(config)) return false;
    return true;
  };

  const addAnotherText = (config: UpsellConfiguration): boolean => {
    const skus = config?.addAnotherTextSku;
    if (!skus?.length) return false;
    return cartStore.items.some((item) => {
      const directMatch = skus.includes(item.sku);
      const bundleSkus = cartStore.cartArray.find((c) => c.sku === item.sku)?.bundleSkus ?? [];
      const bundleMatch = bundleSkus.some((s) => skus.includes(s));
      return directMatch || bundleMatch;
    });
  };

  const isVariantMatch = (
    selection: UpsellConfiguration['choiceGroupSelection'],
  ): selection is UpsellVariantMatch =>
    !!selection && typeof selection === 'object' && 'from' in selection;

  const matchPoolSku = (sku: string | undefined | null, pool: Sku[]): Sku | undefined =>
    sku ? pool.find((baseSku) => isSkuVariantOfBase(sku, baseSku)) : undefined;

  const resolveTriggerVariantSku = (match: UpsellVariantMatch): Sku | undefined => {
    if (getCurrentPath().startsWith('/products')) {
      const selectedSku = matchPoolSku(
        pageDetailsStore.productVariant?.attributes?.sku,
        match.skus,
      );
      if (selectedSku) return selectedSku;
    }
    for (const item of cartStore.items) {
      const matchedSku = matchPoolSku(item.sku, match.skus);
      if (matchedSku) return matchedSku;
    }
    return undefined;
  };

  const findChoiceGroupOption = (item: BundleItem, sku?: Sku) =>
    sku
      ? item.processedProducts?.find(
          (option) => option.attributes.sku === sku && !isProductSoldOut(option),
        )
      : undefined;

  const getTriggerVariantProduct = (config: UpsellConfiguration): ProductComplete | null => {
    const selection = config.choiceGroupSelection;
    if (!isVariantMatch(selection)) return null;
    const choiceGroup = resolveProductWithProcessedItems(
      config.upsellProduct?.product,
    )?.bundleDefinition?.bundleItems.find((item) => item.itemType === 'choice_group');
    if (!choiceGroup) return null;
    return (
      findChoiceGroupOption(choiceGroup, resolveTriggerVariantSku(selection)) ??
      findChoiceGroupOption(choiceGroup, resolveChoiceGroupSelection(selection.fallback)) ??
      null
    );
  };

  const pageBundleChoiceGroupSku = (productRef: string | ProductComplete): Sku | undefined => {
    const resolvedProduct = resolveProduct(productRef);
    if (
      !resolvedProduct?.bundleDefinition ||
      pageBundleStore.currentProduct?.attributes.handle !== resolvedProduct.attributes?.handle
    ) {
      return undefined;
    }
    const choiceGroup = pageBundleStore.bundleGroups.find((g) => g.type === 'choice_group');
    return choiceGroup?.options.find((o) => o.isSelected)?.product.attributes.sku;
  };

  const resolveChoiceGroupTarget = (
    config: UpsellConfiguration,
    productRef: string | ProductComplete,
  ): ChoiceGroupTarget => {
    const selection = config.choiceGroupSelection;
    if (isVariantMatch(selection)) {
      return {
        sku: resolveTriggerVariantSku(selection),
        fallbackSku: resolveChoiceGroupSelection(selection.fallback),
        quantity: selection.quantity,
        variantMatch: true,
      };
    }
    return {
      sku: resolveChoiceGroupSelection(selection) ?? pageBundleChoiceGroupSku(productRef),
    };
  };

  const applyChoiceGroupTarget = (
    builder: ReturnType<typeof useBundleBuilder>,
    product: ProductComplete,
    target: ChoiceGroupTarget,
  ) => {
    if (!target.sku && !target.fallbackSku) return;
    product.bundleDefinition?.bundleItems.forEach((item, index) => {
      if (item.itemType !== 'choice_group') return;
      const chosenOption =
        findChoiceGroupOption(item, target.sku) ?? findChoiceGroupOption(item, target.fallbackSku);
      if (!chosenOption) return;
      const fillCount = target.variantMatch
        ? (target.quantity ?? getChoiceGroupFillCount(item))
        : item.quantity || 1;
      const groupKey = item.groupingName || `group${index}`;
      builder.clearGroup(groupKey);
      builder.addToGroup(groupKey, chosenOption, fillCount);
    });
  };

  const getUpsellSku = (u: UpsellConfiguration): Sku | null => {
    const p = u.upsellProduct?.product;
    if (!p) return null;
    if (typeof p === 'string') return p;
    return p.attributes?.sku || null;
  };

  const activeUpsellIdsInCart = computed(() => {
    const active = new Set<string>();
    for (const item of cartStore.items) {
      const upsellId = item.properties?.['_upsell_id'];
      if (upsellId) active.add(upsellId);
    }
    return active;
  });

  const anyUpsellsInCart = computed(() => activeUpsellIdsInCart.value.size > 0);

  const getCartItemsAfterInvalidRemovals = (invalidUpsellIds: Set<string>) =>
    cartStore.items.filter((item) => !invalidUpsellIds.has(item.properties?.['_upsell_id']));

  const cartItemMatchesProduct = (
    cartItem: (typeof cartStore.items)[number],
    productRef: string | ProductComplete,
    requiredQuantity?: number,
  ) => {
    const identifier = getProductIdentifier(productRef);
    if (!identifier) return false;
    const matchesProduct = cartItem.sku === identifier || cartItem.handle === identifier;
    if (!matchesProduct) return false;
    return requiredQuantity == null || cartItem.quantity >= requiredQuantity;
  };

  const cartRuleItemMatches = (
    item: ProductDetails | UpsellDetails,
    invalidUpsellIds: Set<string>,
  ): boolean => {
    const remainingCartItems = getCartItemsAfterInvalidRemovals(invalidUpsellIds);
    if (isUpsellDetails(item)) {
      return remainingCartItems.some(
        (cartItem) => cartItem.properties?.['_upsell_id'] === item.upsell,
      );
    }
    return remainingCartItems.some((cartItem) =>
      cartItemMatchesProduct(cartItem, item.product, item.quantity),
    );
  };

  const evaluateCartRules = (
    rules: DisplayRequirements | undefined,
    invalidUpsellIds: Set<string>,
  ): boolean => {
    if (!rules) return true;
    const { anyOf, allOf } = normaliseRules(rules);
    if (!anyOf.length && !allOf.length) return true;
    const anyOfPasses =
      !anyOf.length || anyOf.some((item) => cartRuleItemMatches(item, invalidUpsellIds));
    const allOfPasses =
      !allOf.length || allOf.every((item) => cartRuleItemMatches(item, invalidUpsellIds));
    return anyOfPasses && allOfPasses;
  };

  const upsellProductIsInCart = (
    upsell: UpsellConfiguration,
    invalidUpsellIds: Set<string>,
  ): boolean => {
    const upsellSku = getUpsellSku(upsell);
    if (!upsellSku) return false;

    return getCartItemsAfterInvalidRemovals(invalidUpsellIds).some(
      (cartItem) => cartItem.sku === upsellSku,
    );
  };

  const upsellIsActiveInCart = (
    upsell: UpsellConfiguration,
    invalidUpsellIds: Set<string>,
  ): boolean =>
    getCartItemsAfterInvalidRemovals(invalidUpsellIds).some(
      (cartItem) => cartItem.properties?.['_upsell_id'] === upsell.id,
    );

  const collectInvalidUpsellsInCart = () => {
    const invalidUpsellIds = new Set<string>();
    let changed = true;
    while (changed) {
      changed = false;
      for (const upsell of allUpsells) {
        if (invalidUpsellIds.has(upsell.id)) continue;
        const productIsInCart = upsellProductIsInCart(upsell, invalidUpsellIds);
        const activeInCart = upsellIsActiveInCart(upsell, invalidUpsellIds);
        if (!productIsInCart && !activeInCart) continue;
        if (upsell.cartRules?.removeIfOnlyProductInCart && productIsInCart) {
          const remainingCartItems = getCartItemsAfterInvalidRemovals(invalidUpsellIds);
          const onlyThisProductRemains =
            remainingCartItems.length === 1 &&
            remainingCartItems.some((cartItem) => cartItem.sku === getUpsellSku(upsell));
          if (onlyThisProductRemains) {
            invalidUpsellIds.add(upsell.id);
            changed = true;
            continue;
          }
        }
        if (!activeInCart) continue;
        if (!evaluateCartRules(upsell.cartRules?.requiredProductsInCart, invalidUpsellIds)) {
          invalidUpsellIds.add(upsell.id);
          changed = true;
        }
      }
    }
    return invalidUpsellIds;
  };

  const getUpsellLineMatcher = (upsellIds: Set<string>) => {
    const skusToRemove = [...upsellIds]
      .map((id) => allUpsells.find((u) => u.id === id))
      .map((u) => (u ? getUpsellSku(u) : null))
      .filter((sku): sku is Sku => !!sku);
    return (item: ShopifyCartItem) =>
      upsellIds.has(item.properties?.['_upsell_id']) || skusToRemove.includes(item.sku);
  };

  const getUpsellCleanupLineKeys = (): string[] => {
    const isLineToRemove = getUpsellLineMatcher(collectInvalidUpsellsInCart());
    return cartStore.items.filter(isLineToRemove).map(({ key }) => key);
  };

  const removeUpsellsById = async (upsellIds: Set<string>) => {
    if (!upsellIds.size) return false;
    const isLineToRemove = getUpsellLineMatcher(upsellIds);
    if (!cartStore.items.some(isLineToRemove)) return false;
    await cartActions().updateCartItemQuantities((cart) =>
      cart.items.filter(isLineToRemove).map(({ key }) => ({ key, quantity: 0 })),
    );
    return true;
  };

  const validateUpsellsInCart = async (): Promise<boolean> => {
    if (isValidating.value) return false;
    const hasUpsellRuleProductInCart = allUpsells.some((upsell) => {
      if (!upsell.cartRules?.removeIfOnlyProductInCart) return false;
      return upsellProductIsInCart(upsell, new Set());
    });
    if (!anyUpsellsInCart.value && !hasUpsellRuleProductInCart) return false;
    isValidating.value = true;
    try {
      const invalidUpsellIds = collectInvalidUpsellsInCart();
      return await removeUpsellsById(invalidUpsellIds);
    } finally {
      isValidating.value = false;
    }
  };

  const findInheritedLineItemProperties = (
    removeItems: ProductDetails[],
  ): Record<string, string> => {
    const inherited: Record<string, string> = {};
    removeItems.forEach((item) => {
      const identifier = getProductIdentifier(item.product);
      const cartItem = cartStore.items.find((i) => i.sku === identifier || i.handle === identifier);
      Object.assign(inherited, pickCarryOverLineItemProperties(cartItem?.properties));
    });
    return inherited;
  };

  const buildUpsellAtcObject = (
    productRef: string | ProductComplete,
    quantity = 1,
    upsellId?: string,
    choiceGroupTarget: ChoiceGroupTarget = {},
    inheritedProperties: Record<string, string> = {},
  ) => {
    const product = resolveProduct(productRef);
    if (!product?.attributes?.variantId) {
      throw new Error('Product variant ID not found');
    }
    const properties: Record<string, string> = { ...inheritedProperties };
    if (upsellId) properties['_upsell_id'] = upsellId;
    if (product.bundleDefinition) {
      const builder = useBundleBuilder({
        bundleDefinition: product.bundleDefinition,
        autoSelectDefaults: true,
        mode: 'full',
      });

      applyChoiceGroupTarget(builder, product, choiceGroupTarget);
      const cartObject = builder.buildCartObject(product);
      if (cartObject?.items?.properties) {
        Object.assign(properties, cartObject.items.properties);
      } else if (choiceGroupTarget.variantMatch) {
        throw new Error(`Upsell bundle could not be built for ${product.attributes.sku}`);
      }
    }

    return {
      product,
      atcObject: [
        {
          id: product.attributes.variantId,
          quantity,
          properties,
        },
      ],
    };
  };

  const removeProductFromCart = async (productRef: string | ProductComplete, quantity?: number) => {
    const identifier = getProductIdentifier(productRef);
    if (!identifier) return;
    const isMatchingLine = (item: ShopifyCartItem) =>
      item.sku === identifier || item.handle === identifier;
    if (!cartStore.items.some(isMatchingLine)) return;
    await cartActions().updateCartItemQuantities((cart) => {
      const item = cart.items.find(isMatchingLine);
      if (!item) return [];
      const remainingQuantity = quantity && item.quantity > quantity ? item.quantity - quantity : 0;
      return [{ key: item.key, quantity: remainingQuantity }];
    });
  };

  const showUpsellActionError = (text: string) => {
    upsellActionErrorText.value = text;
    setTimeout(() => {
      upsellActionErrorText.value = '';
    }, 2000);
  };

  const handleUpsellAction = async (
    config: UpsellConfiguration,
  ): Promise<RouteLocationRaw | void> => {
    if (isHandlingUpsellAction.value) return;
    isHandlingUpsellAction.value = true;
    lockedUpsell.value = config;
    try {
      const { actions } = config;
      if (!actions) return;
      const removals = getActionRemovals(config);
      if (isReplacementOffer(config) && !removals.length) {
        showUpsellActionError('Not available');
        return;
      }
      if (!hasStockForActions(config)) {
        showUpsellActionError('Not available');
        return;
      }
      const inheritedProperties = findInheritedLineItemProperties(actions.remove || []);
      for (const [index, item] of (actions.add || []).entries()) {
        if (item.product) {
          const { product, atcObject } = buildUpsellAtcObject(
            item.product,
            item.quantity || 1,
            config.id,
            resolveChoiceGroupTarget(config, item.product),
            inheritedProperties,
          );

          const quantity = ref(item.quantity || 1);
          const productRef = ref(product);
          const atcObjectRef = ref(atcObject);

          const { handleAddToCart, buttonLoadingText, clampedQuantity } = useProductAtc({
            product: productRef,
            quantity,
            productPrice: computed(() => product.attributes.price),
            atcObject: atcObjectRef,
            options: {
              soldOut: computed(() => isProductSoldOut(product)),
              sku: computed(() => product.attributes.sku),
              isBundle: computed(() => !!product.bundleDefinition),
              redirectTo: false,
              actionPlan: {
                removals: index === 0 ? removals : [],
                additionSkus: [product.attributes.sku],
              },
            },
          });

          if (clampedQuantity.value < quantity.value) {
            showUpsellActionError('Not available');
            return;
          }

          const { outcome } = await handleAddToCart();
          if (outcome !== 'complete') {
            showUpsellActionError(buttonLoadingText.value || 'Error');
            return;
          }
        }
      }
      if (!actions.add?.length) {
        for (const item of actions.remove || []) {
          if (item.product) {
            await removeProductFromCart(item.product, item.quantity);
          }
        }
      }
      if (actions.redirect) {
        return { path: actions.redirect };
      }
    } finally {
      isHandlingUpsellAction.value = false;
      lockedUpsell.value = null;
    }
  };

  const upsellButtonState = computed<LoadingState>(() => {
    if (isHandlingUpsellAction.value) return 'adding';
    if (upsellActionErrorText.value) return 'error';
    return false;
  });

  const upsellButtonLoadingText = computed(() =>
    upsellButtonState.value === 'error' ? upsellActionErrorText.value : undefined,
  );

  const cartSignature = computed(() =>
    cartStore.items
      .map((i) => `${i.key}:${i.quantity}`)
      .sort()
      .join('|'),
  );
  watch(
    cartSignature,
    async () => {
      await validateUpsellsInCart();
    },
    { flush: 'post', immediate: true },
  );

  const approvedUpsellToDisplay = computed(() => allUpsells.find((u) => approveUpsell(u)) || null);

  const upsellToDisplay = computed(() =>
    isHandlingUpsellAction.value ? lockedUpsell.value : approvedUpsellToDisplay.value,
  );

  const applyPreset = (upsell: UpsellConfiguration) => {
    if (!upsell.preset) return upsell;
    const { deviceBreakpoint } = useDeviceSize();
    const colourPresets = useProductUpsellColourPresets();
    const layoutPresets = useProductUpsellLayoutPresets();
    const colourPreset = upsell.preset.colour ? colourPresets[upsell.preset.colour] : null;
    const layoutEntries = upsell.preset.layout
      ? Array.isArray(upsell.preset.layout)
        ? upsell.preset.layout
        : [upsell.preset.layout]
      : [];
    const mergedLayoutPreset = layoutEntries.reduce(
      (acc, { preset, device = 'default' }) => {
        if (device !== 'default' && device !== deviceBreakpoint.value) return acc;
        const p = layoutPresets[preset];
        const containerStyle = p.style?.mainBody?.containerStyle;
        const resolvedContainerStyle =
          containerStyle && deviceBreakpoint.value in containerStyle
            ? containerStyle[deviceBreakpoint.value]
            : containerStyle;
        const mediaStyle = p.style?.media?.mediaStyle;
        const resolvedMediaStyle =
          mediaStyle && deviceBreakpoint.value in mediaStyle
            ? mediaStyle[deviceBreakpoint.value]
            : mediaStyle;
        return {
          ...acc,
          ...p.style,
          mainBody: {
            ...acc.mainBody,
            ...p.style?.mainBody,
            containerStyle: { ...acc.mainBody?.containerStyle, ...resolvedContainerStyle },
          },
          pill: { ...acc.pill, ...p.style?.pill },
          button: { ...acc.button, ...p.style?.button },
          media: {
            ...acc.media,
            ...p.style?.media,
            mediaStyle: { ...acc.media?.mediaStyle, ...resolvedMediaStyle },
            mediaContainer: { ...acc.media?.mediaContainer, ...p.style?.media?.mediaContainer },
          },
        };
      },
      {} as NonNullable<UpsellProps['style']>,
    );

    return {
      ...upsell,
      upsellData: {
        ...upsell.upsellData,
        style: {
          ...colourPreset?.style,
          ...mergedLayoutPreset,
          ...upsell.upsellData.style,
          mainBody: {
            ...colourPreset?.style?.mainBody,
            ...mergedLayoutPreset.mainBody,
            ...upsell.upsellData.style?.mainBody,
          },
          pill: {
            ...colourPreset?.style?.pill,
            ...mergedLayoutPreset.pill,
            ...upsell.upsellData.style?.pill,
          },
          button: {
            ...colourPreset?.style?.button,
            ...mergedLayoutPreset.button,
            ...upsell.upsellData.style?.button,
          },
          media: {
            ...colourPreset?.style?.media,
            ...mergedLayoutPreset.media,
            ...upsell.upsellData.style?.media,
          },
        },
      },
    };
  };

  const getUpsellToDisplay = (config?: UpsellConfiguration) => {
    const upsell = config ? (approveUpsell(config) ? config : null) : upsellToDisplay.value;
    return upsell ? applyPreset(upsell) : null;
  };

  return {
    resolveProduct,
    resolveProductWithProcessedItems,
    getProductIdentifier,
    isProductInCart,
    approveUpsell,
    addAnotherText,
    getTriggerVariantProduct,
    buildUpsellAtcObject,
    removeProductFromCart,
    handleUpsellAction,
    isHandlingUpsellAction,
    upsellButtonState,
    upsellButtonLoadingText,
    validateUpsellsInCart,
    getUpsellCleanupLineKeys,
    isValidating,
    upsellToDisplay,
    getUpsellToDisplay,
    activeUpsellIdsInCart,
  };
});
